/**
 * BaseCalendar — 공통 캘린더 셸
 *
 * 역할: 월간 그리드(Paper + 요일 헤더 + 날짜 셀) + 네비게이션 바를 제공.
 * 셀 안 콘텐츠는 renderDayBadges / renderDaySuffix render prop으로 소비자가 주입.
 * 주간/일간 뷰는 구현마다 구조가 달라 소비자가 직접 렌더링.
 *
 * Props:
 *  anchor          dayjs  — 현재 기준 날짜 (controlled)
 *  onAnchorChange  fn(dayjs) — 날짜 이동 시 호출
 *  view            "month"|"week"|"day" — 현재 뷰 (controlled)
 *  onViewChange    fn(view) — 뷰 변경 시 호출
 *  supportedViews  string[] — 토글에 표시할 뷰 목록 (기본 ["month","week","day"])
 *  weekStartsOn    "sun"|"mon" — 주 시작 요일 (기본 "sun")
 *  cellHeight      number — 월간 셀 최소 높이 (기본 120)
 *  selectedDay     dayjs|null — 선택된 날짜 (강조)
 *  onDayClick      fn(day: dayjs) — 날짜 셀 클릭
 *  loading         boolean
 *  onRefresh       fn() — 새로고침 버튼 표시 여부도 이 prop으로 결정
 *  title           string — 자동 생성 제목 대신 커스텀 제목
 *  toolbarLeft     ReactNode — 뷰 토글 오른쪽 추가 영역
 *  toolbarRight    ReactNode — 툴바 우측 영역 (범례, 추가 버튼 등)
 *  renderDayBadges fn(day, {isCurrentMonth, isToday, isSelected}) → ReactNode
 *  renderWeekFooter fn(weekDays, {weekIndex}) → ReactNode — 주 행 아래 요약 줄(주별 합계 등). 선택.
 *  hideOutsideWeeks boolean — 이번 달 날짜가 하나도 없는 주 행을 숨긴다(기본 true).
 *    월간 격자는 항상 6주(42칸)라, 달에 따라 마지막 한 줄이 통째로 '다음 달' 이 되어 빈 줄만 남는다.
 *    달력은 '그 달' 을 보는 화면이므로 기본으로 감춘다. 6주 고정 높이가 필요한 화면만 false 로 끈다.
 *  renderDaySuffix fn(day, {isCurrentMonth}) → ReactNode — 날짜 숫자 오른쪽 슬롯
 *  getDayCellSx    fn(day, {isCurrentMonth, isToday, isSelected}) → sx — 날짜 칸 자체의 스타일 덮어쓰기.
 *    기본 sx 뒤에 펼쳐지므로 bgcolor·cursor 등을 소비자가 바꿀 수 있다(안 넘기면 기존 동작 그대로).
 */

import React, { useState, useMemo } from "react";
import {
    Box, CircularProgress, IconButton, Paper, Popover,
    Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography, Button,
} from "@mui/material";
import ChevronLeftRoundedIcon  from "@mui/icons-material/ChevronLeftRounded";
import ActionButton from "../common/ActionButton";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import RefreshRoundedIcon      from "@mui/icons-material/RefreshRounded";
import dayjs from "dayjs";
import LoadingDots from "../common/LoadingDots";

/* ── 요일 라벨 ─────────────────────────────────────── */
const LABELS_SUN = ["일", "월", "화", "수", "목", "금", "토"];
const LABELS_MON = ["월", "화", "수", "목", "금", "토", "일"];

function dowColor(idx, weekStartsOn) {
    if (weekStartsOn === "mon") {
        return idx === 5 ? "#3b82f6" : idx === 6 ? "#ef4444" : "#64748b";
    }
    return idx === 0 ? "#ef4444" : idx === 6 ? "#3b82f6" : "#64748b";
}

/* ── 월간 셀 생성 (42칸) ────────────────────────────── */
function buildMonthCells(anchor, weekStartsOn) {
    const first = anchor.startOf("month");
    const daysInMonth = first.daysInMonth();
    const rawDow = first.day(); // 0=일
    const offset = weekStartsOn === "mon" ? (rawDow + 6) % 7 : rawDow;

    const cells = [];
    for (let i = offset - 1; i >= 0; i--) cells.push(first.subtract(i + 1, "day"));
    for (let d = 1; d <= daysInMonth; d++) cells.push(first.date(d));
    const last = first.date(daysInMonth);
    while (cells.length < 42) cells.push(last.add(cells.length - daysInMonth - offset + 1, "day"));
    return cells;
}

/* ── 제목 자동 생성 ─────────────────────────────────── */
function autoTitle(view, anchor) {
    if (view === "month") return anchor.format("YYYY년 M월");
    if (view === "week") {
        const s = anchor.startOf("week"), e = anchor.endOf("week");
        return `${s.format("YYYY.MM.DD")} ~ ${e.format("MM.DD")}`;
    }
    return anchor.format("YYYY년 M월 D일 (ddd)");
}

const VIEW_LABELS = { month: "월간", week: "주간", day: "일간" };

/* ── 다중일(연속) 일정 바 — 주 행 위에 절대배치로 여러 칸을 가로지름 ── */
function SpanBar({ seg, laneH }) {
    const { span, startCol, endCol, contStart, contEnd, lane } = seg;
    return (
        <Box
            onClick={(e) => { e.stopPropagation(); span.onClick?.(span); }}
            title={span.label}
            sx={{
                position: "absolute",
                top: lane * laneH,
                height: laneH - 3,
                left: `calc(${(startCol / 7) * 100}% + 3px)`,
                width: `calc(${((endCol - startCol + 1) / 7) * 100}% - 6px)`,
                bgcolor: span.dimmed ? "#f1f5f9" : (span.bg || "#dbeafe"),
                borderLeft: contStart ? "none" : `3px solid ${span.dimmed ? "#cbd5e1" : (span.bar || "#2563eb")}`,
                borderTopLeftRadius: contStart ? 0 : 5,
                borderBottomLeftRadius: contStart ? 0 : 5,
                borderTopRightRadius: contEnd ? 0 : 5,
                borderBottomRightRadius: contEnd ? 0 : 5,
                display: "flex", alignItems: "center", px: 0.6,
                cursor: "pointer", pointerEvents: "auto", overflow: "hidden",
                opacity: span.dimmed ? 0.5 : 1,
                "&:hover": { filter: "brightness(0.95)" },
            }}
        >
            {!contStart && (
                <Typography sx={{
                    fontSize: 11, fontWeight: 700,
                    color: span.dimmed ? "#94a3b8" : (span.text || "#1e3a8a"),
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                    {span.label}
                </Typography>
            )}
        </Box>
    );
}

/* ── 메인 컴포넌트 ───────────────────────────────────── */
export default function BaseCalendar({
    anchor,
    onAnchorChange,
    view = "month",
    onViewChange,
    supportedViews = ["month", "week", "day"],
    weekStartsOn = "sun",
    cellHeight = 120,
    selectedDay = null,
    onDayClick,
    loading = false,
    onRefresh,
    title,
    toolbarLeft,
    toolbarRight,
    renderDayBadges,
    renderWeekFooter,
    hideOutsideWeeks = true,
    renderDaySuffix,
    getDayCellSx,
    spans = [],
    spanLaneHeight = 20,
}) {
    const today = useMemo(() => dayjs(), []);
    const cells = useMemo(() => buildMonthCells(anchor, weekStartsOn), [anchor, weekStartsOn]);
    const labels = weekStartsOn === "mon" ? LABELS_MON : LABELS_SUN;
    const displayTitle = title ?? autoTitle(view, anchor);

    /* ── 연속 바(다중일 일정) 레이아웃 ──
     * 날짜 숫자 행 아래(SPAN_TOP)에 주 단위로 절대배치하고, 셀에는 그만큼 공간을 비워 배지가 겹치지 않게 한다. */
    const SPAN_TOP = 36; // 셀 padding(4.8) + 날짜 숫자 행(28+여백) ≈ 36px
    const weeks = useMemo(() => {
        const w = [];
        for (let i = 0; i < cells.length; i += 7) w.push(cells.slice(i, i + 7));
        // 월간 격자는 항상 6주(42칸)로 채워지므로, 달에 따라 마지막 한 줄이 통째로 '다음 달' 이 된다.
        // hideOutsideWeeks 면 그런 줄을 지운다 — 이번 달만 보려는 화면에서 빈 줄이 자리만 차지하기 때문.
        // (앞뒤로 걸친 주는 이번 달 날짜를 포함하므로 그대로 둔다.)
        if (!hideOutsideWeeks) return w;
        return w.filter(week => week.some(d => d.month() === anchor.month() && d.year() === anchor.year()));
    }, [cells, hideOutsideWeeks, anchor]);
    const normSpans = useMemo(() => (spans || []).map((s) => ({
        ...s,
        _start: dayjs(s.start).startOf("day"),
        _end: dayjs(s.end ?? s.start).startOf("day"),
    })), [spans]);
    const layoutWeek = (weekDays) => {
        const wStart = weekDays[0].startOf("day");
        const wEnd = weekDays[6].startOf("day");
        const segs = normSpans
            .filter((s) => !s._end.isBefore(wStart) && !s._start.isAfter(wEnd))
            .map((s) => {
                const segStart = s._start.isBefore(wStart) ? wStart : s._start;
                const segEnd = s._end.isAfter(wEnd) ? wEnd : s._end;
                return {
                    span: s,
                    startCol: segStart.diff(wStart, "day"),
                    endCol: segEnd.diff(wStart, "day"),
                    contStart: s._start.isBefore(wStart),
                    contEnd: s._end.isAfter(wEnd),
                    lane: 0,
                };
            })
            .sort((a, b) => a.startCol - b.startCol || b.endCol - a.endCol);
        const lanes = [];
        segs.forEach((seg) => {
            let lane = 0;
            while (lanes[lane] && !lanes[lane].every((o) => seg.endCol < o.startCol || seg.startCol > o.endCol)) lane++;
            (lanes[lane] = lanes[lane] || []).push(seg);
            seg.lane = lane;
        });
        return { segs, laneCount: lanes.length };
    };

    const renderDayCell = (day, idx, opts = {}) => {
        const { bandH = 0, flat = false, isLastRow = false, colIdx = idx % 7 } = opts;
        const isCurrentMonth = day.month() === anchor.month() && day.year() === anchor.year();
        const isToday        = day.isSame(today, "day");
        const isSelected     = !!selectedDay && day.isSame(selectedDay, "day");
        const rawDow         = day.day();
        const displayIdx     = weekStartsOn === "mon" ? (rawDow === 0 ? 6 : rawDow - 1) : rawDow;
        return (
            <Box
                key={idx}
                onClick={() => onDayClick?.(day)}
                sx={{
                    minHeight: cellHeight,
                    p: 0.6, minWidth: 0,
                    // 세로 flex — 배지 슬롯이 남은 높이를 차지해, 소비자가 'mt:auto' 로 요약 줄을
                    // 칸 <b>맨 아래</b>에 붙일 수 있게 한다. 항목 수가 칸마다 달라도 요약 줄의 높이가
                    // 한 줄로 맞아 가로로 시선이 흐르지 않는다.
                    display: "flex", flexDirection: "column",
                    borderRight:  (flat ? idx % 7 !== 6 : colIdx < 6) ? "1px solid #f1f5f9" : "none",
                    borderBottom: (flat ? idx < 35 : !isLastRow)     ? "1px solid #f1f5f9" : "none",
                    cursor: "pointer",
                    bgcolor: isSelected ? "#eff6ff" : "transparent",
                    transition: "background 0.1s",
                    "&:hover": { bgcolor: isSelected ? "#eff6ff" : "#f8fafc" },
                    // 소비자가 칸 자체의 스타일을 덮어쓸 수 있는 슬롯 (안 넘기면 기존 그대로).
                    // 배지만으로는 '칸 전체'의 성격을 못 나타내는 경우가 있어서 뒀다 —
                    // 자금 달력이 잔액 마이너스(자금 부족)인 날 칸을 통째로 붉게 칠하는 용도.
                    ...(getDayCellSx?.(day, { isCurrentMonth, isToday, isSelected }) || {}),
                }}
            >
                {/* 날짜 숫자 행 */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.4, minWidth: 0 }}>
                    <Box sx={{
                        width: 28, height: 28, borderRadius: "50%",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        bgcolor: isToday ? "#2563eb" : "transparent", flexShrink: 0,
                    }}>
                        <Typography sx={{
                            fontSize: 14, fontWeight: isToday ? 700 : 600,
                            color: isToday ? "#fff" : !isCurrentMonth ? "#d1d5db" : dowColor(displayIdx, weekStartsOn),
                        }}>
                            {day.date()}
                        </Typography>
                    </Box>
                    {renderDaySuffix?.(day, { isCurrentMonth, isToday, isSelected })}
                </Box>
                {/* 연속 바 예약 공간 */}
                {bandH > 0 && <Box sx={{ height: bandH }} />}
                {/* 배지 슬롯 — 남은 높이를 채운다(위 flexDirection:column 과 세트).
                    안쪽에서 'mt:auto' 를 쓰면 그 요소가 칸 바닥에 붙는다. */}
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    {renderDayBadges?.(day, { isCurrentMonth, isToday, isSelected })}
                </Box>
            </Box>
        );
    };

    /* 제목 클릭 → 날짜 선택 Popover */
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);

    const nav = (dir) => {
        const unit = view === "month" ? "month" : view === "week" ? "week" : "day";
        onAnchorChange?.(anchor.add(dir, unit));
    };

    const handleTitleClick = (e) => {
        setPickerAnchorEl(e.currentTarget);
    };

    const handlePickerChange = (e) => {
        const val = e.target.value;
        if (!val) return;
        onAnchorChange?.(dayjs(val));
        setPickerAnchorEl(null);
        // 일간 뷰가 지원되는 경우 선택 날짜로 바로 이동
        if (supportedViews.includes("day")) onViewChange?.("day");
    };

    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            {/* ── 네비게이션 바 ── */}
            <Stack
                direction="row" alignItems="center" justifyContent="space-between"
                flexWrap="wrap" gap={1} mb={1.5} sx={{ width: "100%", minWidth: 0 }}
            >
                <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                    <IconButton size="small" onClick={() => nav(-1)}>
                        <ChevronLeftRoundedIcon />
                    </IconButton>

                    {/* 제목 클릭 → DatePicker 팝오버 (모든 뷰) */}
                    <ActionButton
                        variant="secondary"
                        startIcon={false}
                        onClick={handleTitleClick}
                        sx={{
                            fontWeight: 700, fontSize: 15, minWidth: 160,
                            color: "#1e293b", borderColor: "transparent",
                            "&:hover": { borderColor: "transparent", bgcolor: "#f1f5f9", color: "#1e293b" },
                        }}
                        label={displayTitle}
                    />

                    <IconButton size="small" onClick={() => nav(1)}>
                        <ChevronRightRoundedIcon />
                    </IconButton>

                    <ActionButton
                        variant="secondary"
                        startIcon={false}
                        onClick={() => onAnchorChange?.(dayjs())}
                        label="오늘"
                        sx={{ fontSize: 12, color: "#2563eb", minWidth: "auto",
                            borderColor: "transparent",
                            "&:hover": { bgcolor: "#eff6ff", borderColor: "transparent" } }}
                    />

                    {onRefresh && (
                        <Tooltip title="새로고침">
                            <IconButton size="small" onClick={onRefresh} disabled={loading}>
                                <RefreshRoundedIcon sx={{ fontSize: 18, color: "#64748b" }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {supportedViews.length > 1 && (
                        <Box sx={{ ml: 1, pl: 1, borderLeft: "1px solid #e2e8f0" }}>
                            <ToggleButtonGroup
                                value={view} exclusive size="small"
                                onChange={(_, v) => { if (v) onViewChange?.(v); }}
                                sx={{
                                    gap: 1,
                                    "& .MuiToggleButtonGroup-grouped": {
                                        border: "1px solid rgba(0,0,0,0.12) !important",
                                        borderRadius: "6px !important",
                                        "&:not(:last-of-type)": { mr: 0 },
                                    },
                                }}
                            >
                                {supportedViews.map(v => (
                                    <ToggleButton key={v} value={v} sx={{
                                        px: 1.5, py: 0.4, fontSize: 12, fontWeight: 700,
                                        textTransform: "none", borderRadius: "6px !important",
                                        "&.Mui-selected": { bgcolor: "#2563eb", color: "#fff", "&:hover": { bgcolor: "#1d4ed8" } },
                                    }}>
                                        {VIEW_LABELS[v]}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                    )}

                    {toolbarLeft}
                </Stack>

                {toolbarRight && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {toolbarRight}
                    </Stack>
                )}
            </Stack>

            {/* ── 월간 그리드 ── */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                    <LoadingDots />
                </Box>
            ) : view === "month" ? (
                <Paper elevation={0} sx={{
                    width: "100%", minWidth: 0,
                    border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden",
                }}>
                    {/* 요일 헤더 */}
                    <Box sx={{
                        display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                        bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0",
                    }}>
                        {labels.map((d, i) => (
                            <Box key={d} sx={{ py: 1, textAlign: "center", minWidth: 0 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: dowColor(i, weekStartsOn) }}>
                                    {d}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* 날짜 셀 — 항상 주 단위 행으로 렌더. 다중일 일정(spans)은 셀 위로 연속 바 자동 표시 */}
                    {weeks.map((weekDays, wi) => {
                        const { segs, laneCount } = layoutWeek(weekDays);
                        const bandH = laneCount * spanLaneHeight;
                        return (
                            <React.Fragment key={wi}>
                                <Box sx={{ position: "relative", display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", width: "100%", minWidth: 0 }}>
                                    {weekDays.map((day, di) => renderDayCell(day, wi * 7 + di, { bandH, isLastRow: wi === weeks.length - 1, colIdx: di }))}
                                    {bandH > 0 && (
                                        <Box sx={{ position: "absolute", top: SPAN_TOP, left: 0, right: 0, height: bandH, pointerEvents: "none" }}>
                                            {segs.map((seg) => <SpanBar key={`${seg.span.id}-${wi}`} seg={seg} laneH={spanLaneHeight} />)}
                                        </Box>
                                    )}
                                </Box>
                                {/* 주 단위 요약 줄 — 소비자가 renderWeekFooter 를 준 경우에만 그린다.
                                    주별 합계처럼 "한 행 전체에 대한 값"을 놓을 자리가 없어서 추가한 슬롯이며,
                                    안 넘기면 아무것도 렌더되지 않아 기존 달력들은 그대로다. */}
                                {renderWeekFooter?.(weekDays, { weekIndex: wi })}
                            </React.Fragment>
                        );
                    })}
                </Paper>
            ) : null /* 주간/일간은 소비자가 직접 렌더링 */}

            {/* 일간 뷰 제목 클릭 → 날짜 선택 팝오버 */}
            <Popover
                open={Boolean(pickerAnchorEl)}
                anchorEl={pickerAnchorEl}
                onClose={() => setPickerAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                PaperProps={{ sx: { borderRadius: 1.5, p: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 220 } }}
            >
                <Stack spacing={1}>
                    <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>날짜 선택</Typography>
                    <input
                        type="date"
                        defaultValue={anchor.format("YYYY-MM-DD")}
                        onChange={handlePickerChange}
                        style={{
                            fontSize: 14, padding: "8px 12px",
                            border: "1px solid #e2e8f0", borderRadius: 8,
                            outline: "none", cursor: "pointer",
                            width: "100%", boxSizing: "border-box",
                        }}
                    />
                </Stack>
            </Popover>
        </Box>
    );
}
